import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { SaveCancelTop } from "../../components/PrimaryButton";
import Input from "../../components/Input";
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from "../../components/LoadingLogo";
import Breadcrumbs from "../../components/Breadcrumbs";
import { securePut, securePost } from "../../config/api";

const ClientForm = ({
  title,
  initialValues = {},
  baseFields = [],
  subscriptionFields = [],
  hostingFields = [],
  onSuccess,
}) => {
  const [formValues, setFormValues] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const baseFieldsReady = baseFields.every(field =>
      !field.options || (field.options && field.options.length > 0)
    );
    setLoading(!baseFieldsReady);
  }, [baseFields]);

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setFormValues({
        companyName: initialValues.companyName ?? "",
        phoneNumber: initialValues.phoneNumber ?? "",
        contactFirstName: initialValues.contactFirstName ?? "",
        contactLastName: initialValues.contactLastName ?? "",
        contactEmail: initialValues.contactEmail ?? "",
        preferredLanguage: initialValues.preferredLanguage ?? "",
        password: "",
        confirmPassword: "",
      });
    } else {
      setFormValues({
        companyName: "",
        phoneNumber: "",
        contactFirstName: "",
        contactLastName: "",
        contactEmail: "",
        preferredLanguage: "",
        password: "",
        confirmPassword: "",
      });
    }
  }, [initialValues]);

  const isModify = initialValues?.id != null && initialValues?.id !== "";

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fieldsToValidate = isModify
      ? baseFields.filter((f) => f.name !== "password" && f.name !== "confirmPassword")
      : baseFields;
    const requiredFields = fieldsToValidate.filter((f) => f.required);
    const missing = requiredFields.filter((f) => !formValues[f.name] || String(formValues[f.name]).trim() === "");
    if (missing.length > 0) {
      showSnackbar(t("signup_fields.required_fields"), "error");
      return;
    }
    if (!isModify && formValues.password !== formValues.confirmPassword) {
      showSnackbar(t("signup_fields.password_mismatch"), "error");
      return;
    }

    const payload = {
      companyName: formValues.companyName,
      phoneNumber: formValues.phoneNumber,
      contactFirstName: formValues.contactFirstName,
      contactLastName: formValues.contactLastName,
      contactEmail: formValues.contactEmail,
      preferredLanguage: formValues.preferredLanguage,
    };
    if (!isModify) {
      payload.password = formValues.password;
    }

    try {
      if (isModify) {
        const grayClientId = initialValues.id;
        if (grayClientId == null || grayClientId === "") {
          showSnackbar(t("errors.generic"), "error");
          return;
        }
        await securePut(`/grayClients/${grayClientId}`, payload, showSnackbar);
        showSnackbar(t("confirmation.clientsEdit") || "Client updated", "success");
        onSuccess?.();
        history.push({
          pathname: "/clients",
          state: {
            filters: location.state?.filters || {},
            page: location.state?.page || 0,
            size: location.state?.size || 100,
            showSnackbar: true,
            snackbarMessage: t("confirmation.clientsEdit"),
            snackbarSeverity: "success",
          },
        });
        return;
      }

      await securePost("/grayClients", {
        companyName: payload.companyName,
        contactFirstName: payload.contactFirstName,
        contactLastName: payload.contactLastName,
        contactEmail: payload.contactEmail,
        phoneNumber: payload.phoneNumber,
        preferredLanguage: payload.preferredLanguage,
        password: payload.password,
      }, showSnackbar);

      showSnackbar(t("confirmation.clientsAdd"), "success");
      onSuccess?.();
      history.push({
        pathname: "/clients",
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 100,
          showSnackbar: true,
          snackbarMessage: t("confirmation.clientsAdd"),
          snackbarSeverity: "success",
        },
      });
    } catch (error) {
      console.error(isModify ? "Error updating grayClient:" : "Error submitting client signup:", error);
      showSnackbar(t("errors.generic"), "error");
    }
  };

  const handleCancel = () => {
    history.push({
      pathname: "/clients",
      state: {
        filters: location.state?.filters || {},
        page: location.state?.page || 0,
        size: location.state?.size || 100,
      },
    });
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>GrayFox - {t("titles.clients")}</title>
      </Helmet>
      <div className="flex flex-col space-y-2">
        <Breadcrumbs />
        <div className="flex justify-between items-center">
          <h2 className="text-[32px] font-bold text-black">{title}</h2>
          <SaveCancelTop onCancel={handleCancel} onSave={handleSubmit} />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-[300px]">
          <LoadingLogo />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-6">
            {baseFields.length > 0 && (
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {(isModify ? baseFields.filter((f) => f.name !== "password" && f.name !== "confirmPassword") : baseFields).map((field, index) => (
                    <Input
                      key={field.name || index}
                      label={field.label}
                      name={field.name}
                      type={field.type || "text"}
                      value={formValues[field.name] ?? ""}
                      onChange={(value) => handleInputChange(field.name, value)}
                      options={field.options}
                      required={field.required}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </form>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default ClientForm;
